/**
 * Shows Storefront-calculated discount-code prices as native sale UI on PLP cards and PDPs.
 * Display-only: does not mutate the shopper's Ajax cart.
 */
(function () {
  const config = window.theme && theme.discountPrices;
  if (!config || !config.enabled || !config.storefrontToken) return;

  const CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
  const CACHE_PREFIX = 'discount-preview:';
  const API_VERSION = config.apiVersion || '2026-04';

  const CART_CREATE_MUTATION = `mutation DiscountPreviewCart($lines: [CartLineInput!]!, $discountCodes: [String!]) {
    cartCreate(input: { lines: $lines, discountCodes: $discountCodes }) {
      cart {
        id
        discountCodes {
          code
          applicable
        }
        lines(first: 100) {
          edges {
            node {
              merchandise {
                ... on ProductVariant {
                  id
                }
              }
              cost {
                amountPerQuantity {
                  amount
                }
                totalAmount {
                  amount
                }
              }
              discountAllocations {
                discountedAmount {
                  amount
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }`;

  let allocationMap = {};
  let activeCode = null;
  let fetchPromise = null;
  let fetchKey = null;
  let observerTimer = null;

  function getCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch (e) {
      return match[1];
    }
  }

  function validateCode(code) {
    if (!code || typeof code !== 'string') return null;
    const trimmed = code.trim();
    if (!CODE_PATTERN.test(trimmed)) return null;
    return trimmed;
  }

  function formatMoney(cents) {
    const settings = theme.settings || {};
    const format = settings.productCurrencyCodeEnabled
      ? settings.moneyWithCurrencyFormat
      : settings.moneyFormat || settings.moneyWithCurrencyFormat;
    if (!format) return (cents / 100).toFixed(2);

    const value = (Math.round(cents) / 100).toFixed(2);
    const parts = value.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    const centsPart = parts[1] || '00';
    const amountWithCommaSeparator = `${parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')},${centsPart}`;
    const amountNoDecimals = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');

    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithCommaSeparator)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_with_currency_separator\s*\}\}/g, `${dollars}.${centsPart}`)
      .replace(/\{\{\s*amount\s*\}\}/g, `${dollars}.${centsPart}`);
  }

  function decimalToCents(amount) {
    return Math.round(parseFloat(amount, 10) * 100);
  }

  function variantGid(id) {
    return `gid://shopify/ProductVariant/${id}`;
  }

  function variantIdFromGid(gid) {
    if (!gid) return null;
    const parts = String(gid).split('/');
    return parts[parts.length - 1];
  }

  function getShopDomain() {
    if (window.Shopify && Shopify.shop) return Shopify.shop;
    try {
      const baseHost = new URL(theme.routes.base).hostname;
      if (baseHost && baseHost !== '127.0.0.1' && baseHost !== 'localhost') return baseHost;
    } catch (e) {
      /* fall through */
    }
    return window.location.hostname;
  }

  function collectVariantIds() {
    const ids = new Set();

    document.querySelectorAll('product-block[data-variant-id]').forEach((el) => {
      if (el.dataset.variantId) ids.add(String(el.dataset.variantId));
    });

    document.querySelectorAll('[data-discount-price-root]').forEach((root) => {
      const script = root.querySelector('[data-discount-variants]');
      if (script) {
        try {
          const variants = JSON.parse(script.textContent);
          variants.forEach((v) => {
            if (v && v.id) ids.add(String(v.id));
          });
        } catch (e) {
          /* fail closed */
        }
      } else if (root.dataset.variantId) {
        ids.add(String(root.dataset.variantId));
      }
    });

    return Array.from(ids);
  }

  function cacheKey(code) {
    return `${CACHE_PREFIX}${code}`;
  }

  function readCache(code) {
    try {
      const raw = sessionStorage.getItem(cacheKey(code));
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  function writeCache(code, map) {
    try {
      sessionStorage.setItem(cacheKey(code), JSON.stringify(map));
    } catch (e) {
      /* ignore quota */
    }
  }

  function loadCachedAllocations(code) {
    allocationMap = Object.assign({}, readCache(code), allocationMap);
  }

  async function fetchAllocations(code, variantIds) {
    if (!variantIds.length) return {};

    const endpoint = `https://${getShopDomain()}/api/${API_VERSION}/graphql.json`;
    const map = {};
    const chunkSize = 100;

    for (let i = 0; i < variantIds.length; i += chunkSize) {
      const chunk = variantIds.slice(i, i + chunkSize);
      const lines = chunk.map((id) => ({
        merchandiseId: variantGid(id),
        quantity: 1
      }));

      let response;
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': config.storefrontToken
          },
          body: JSON.stringify({
            query: CART_CREATE_MUTATION,
            variables: {
              lines,
              discountCodes: [code]
            }
          })
        });
      } catch (e) {
        // Leave ids uncached so a later refresh can retry.
        return map;
      }

      if (!response.ok) return map;

      let payload;
      try {
        payload = await response.json();
      } catch (e) {
        return map;
      }

      // Auth/scope/schema errors: mark checked so we fail closed without a retry storm.
      if (payload.errors && payload.errors.length) {
        chunk.forEach((id) => { map[id] = null; });
        return map;
      }

      const result = payload && payload.data && payload.data.cartCreate;
      if (!result || !result.cart || (result.userErrors && result.userErrors.length)) {
        chunk.forEach((id) => { map[id] = null; });
        return map;
      }

      const codes = result.cart.discountCodes || [];
      const codePresent = codes.some((entry) => (
        entry.code && entry.code.toLowerCase() === code.toLowerCase()
      ));

      // Mark requested ids checked (even with no allocation) to avoid refetch loops.
      chunk.forEach((id) => { map[id] = null; });
      if (!codePresent) return map;

      const edges = (result.cart.lines && result.cart.lines.edges) || [];
      edges.forEach((edge) => {
        const node = edge.node;
        if (!node || !node.merchandise) return;

        const id = variantIdFromGid(node.merchandise.id);
        if (!id) return;

        const allocations = node.discountAllocations || [];
        let discountCents = 0;
        allocations.forEach((alloc) => {
          if (alloc.discountedAmount && alloc.discountedAmount.amount) {
            discountCents += decimalToCents(alloc.discountedAmount.amount);
          }
        });

        if (discountCents <= 0) {
          map[id] = null;
          return;
        }

        const originalCents = decimalToCents(node.cost.amountPerQuantity.amount);
        const discountedCents = Math.max(0, originalCents - discountCents);

        map[id] = {
          originalCents,
          discountedCents
        };
      });
    }

    return map;
  }

  function ensurePriceWas(priceEl) {
    let was = priceEl.querySelector('.price__was');
    if (was) return was;

    const defaultWrap = priceEl.querySelector('.price__default');
    if (!defaultWrap) return null;

    was = document.createElement('span');
    was.className = 'price__was';
    const current = defaultWrap.querySelector('.price__current');
    if (current && current.nextSibling) {
      defaultWrap.insertBefore(was, current.nextSibling);
    } else {
      defaultWrap.appendChild(was);
    }
    return was;
  }

  function applySalePrice(priceEl, originalCents, discountedCents) {
    if (!priceEl) return;

    const currentEl = priceEl.querySelector('.price__current');
    if (!currentEl) return;

    currentEl.textContent = formatMoney(discountedCents);

    const wasEl = ensurePriceWas(priceEl);
    if (wasEl) wasEl.textContent = formatMoney(originalCents);

    priceEl.classList.add('price--on-sale');
  }

  function applyCardSaleLabel(block, originalCents, discountedCents) {
    const settings = theme.settings || {};
    const strings = theme.strings || {};

    if (settings.prodSaleShow) {
      const priceWrap = block.querySelector('.product-price');
      if (priceWrap && !priceWrap.querySelector('.price-label--sale')) {
        const label = document.createElement('span');
        label.className = 'price-label price-label--sale';
        label.dataset.discountLabel = 'true';
        label.textContent = strings.productsProductSale || 'Sale';
        priceWrap.appendChild(label);
      }
    }

    if (!settings.prodReductionShow) return;

    const percent = Math.round((1 - (discountedCents / originalCents)) * 100);
    if (percent <= 0) return;

    const imageWrap = block.querySelector('.image-label-wrap');
    if (!imageWrap) return;

    let container = imageWrap.querySelector('.product-label-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'product-label-container';
      imageWrap.appendChild(container);
    }

    let saleLabel = container.querySelector('.product-label--sale[data-discount-label]');
    if (!saleLabel) {
      saleLabel = container.querySelector('.product-label--sale');
    }
    if (!saleLabel) {
      saleLabel = document.createElement('span');
      saleLabel.className = 'product-label product-label--sale';
      saleLabel.dataset.discountLabel = 'true';
      const inner = document.createElement('span');
      saleLabel.appendChild(inner);
      container.appendChild(saleLabel);
    }

    const inner = saleLabel.querySelector('span') || saleLabel;
    if (settings.prodReductionType === 'percent') {
      const template = strings.productsProductPercentReduction || '[AMOUNT]% off';
      inner.textContent = template.replace('[AMOUNT]', String(percent));
    } else {
      inner.textContent = formatMoney(originalCents - discountedCents) + ' off';
    }
  }

  function getMessageEl(root) {
    const sibling = root.nextElementSibling;
    if (sibling && sibling.hasAttribute('data-discount-message')) return sibling;
    return root.querySelector('[data-discount-message]');
  }

  function buildMessage(code) {
    const template = config.message || 'Code {{ code }} applied';
    return template.replace(/\{\{\s*code\s*\}\}/gi, code);
  }

  function applyToCards() {
    document.querySelectorAll('product-block[data-variant-id]').forEach((block) => {
      const id = String(block.dataset.variantId);
      const entry = allocationMap[id];
      if (!entry) return;

      const priceEl = block.querySelector('.product-price .price');
      if (!priceEl) return;

      applySalePrice(priceEl, entry.originalCents, entry.discountedCents);
      applyCardSaleLabel(block, entry.originalCents, entry.discountedCents);
      block.dataset.discountApplied = 'true';
    });
  }

  function applyToPdp(variantId) {
    document.querySelectorAll('[data-discount-price-root]').forEach((root) => {
      const messageEl = getMessageEl(root);
      const priceEl = root.querySelector('.product-price .price');
      const id = String(variantId || root.dataset.variantId || '');
      const entry = id ? allocationMap[id] : null;

      if (entry) {
        applySalePrice(priceEl, entry.originalCents, entry.discountedCents);
        root.dataset.discountApplied = 'true';
        if (messageEl && activeCode) {
          messageEl.textContent = buildMessage(activeCode);
          messageEl.hidden = false;
        }
      } else if (messageEl) {
        messageEl.textContent = '';
        messageEl.hidden = true;
        root.dataset.discountApplied = 'false';
      }
    });
  }

  function paintKnown() {
    applyToCards();
    applyToPdp();
  }

  async function refresh() {
    const code = validateCode(getCookie('discount_code'));
    if (!code) return;

    if (activeCode !== code) {
      activeCode = code;
      allocationMap = {};
    }

    loadCachedAllocations(code);
    paintKnown();

    const variantIds = collectVariantIds();
    if (!variantIds.length) return;

    const missingIds = variantIds.filter((id) => !(id in allocationMap));
    if (!missingIds.length) {
      paintKnown();
      return;
    }

    const key = `${code}:${missingIds.slice().sort().join(',')}`;

    if (!fetchPromise || fetchKey !== key) {
      fetchKey = key;
      fetchPromise = fetchAllocations(code, missingIds)
        .then((map) => {
          Object.keys(map).forEach((id) => {
            allocationMap[id] = map[id];
          });
          writeCache(code, allocationMap);
          return map;
        })
        .finally(() => {
          if (fetchKey === key) {
            fetchPromise = null;
            fetchKey = null;
          }
        });
    }

    await fetchPromise;
    paintKnown();
  }

  function scheduleRefresh() {
    if (observerTimer) clearTimeout(observerTimer);
    observerTimer = setTimeout(() => {
      refresh();
    }, 150);
  }

  function onVariantChange(evt) {
    const variant = evt.detail && evt.detail.variant;
    if (!variant || !variant.id) return;

    const id = String(variant.id);

    document.querySelectorAll('[data-discount-price-root]').forEach((root) => {
      root.dataset.variantId = id;
      if (typeof variant.price === 'number') {
        root.dataset.price = String(variant.price);
      }
    });

    if (id in allocationMap) {
      applyToPdp(id);
    } else {
      scheduleRefresh();
    }
  }

  function nodeHasProductBlocks(node) {
    if (!node || node.nodeType !== 1) return false;
    return node.matches?.('product-block, [data-discount-price-root], .product-grid, .main-search__results, filter-container')
      || node.querySelector?.('product-block, [data-discount-price-root]');
  }

  function initObserver() {
    if (typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        if (mutation.type !== 'childList') return false;
        return Array.from(mutation.addedNodes).some(nodeHasProductBlocks);
      });
      if (relevant) {
        // Paint anything already known immediately, then fetch missing.
        paintKnown();
        scheduleRefresh();
      }
    });

    // Watch body so collection/search AJAX and header predictive search all apply.
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    if (!validateCode(getCookie('discount_code'))) return;

    document.addEventListener('on:variant:change', onVariantChange);
    initObserver();
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
